import { useCallback, useMemo } from "react"
import type { Dispatch, SetStateAction } from "react"
import type {
  TemplateConfiguration,
  TemplateField,
  TemplateSection,
} from "shared/api/templateService"
import { GENERAL_FIELD_STATE_MAP } from "modules/permit/utils/permitFieldMapping"
import {
  resolveTemplateFieldKeys,
  resolveTemplateFieldLabelKeys,
} from "modules/permit/components/permit-request/utils/templateFieldUtils"

// ฟิลด์ที่มี UI เฉพาะ (หรือ state handling พิเศษ) ที่เราจะไม่เรนเดอร์ซ้ำแบบ generic
const RENDERED_GENERAL_FIELD_CODES = new Set<string>([
  "WORKDESCR",
  "PROJNO",
  "WORKLOCATION",
  "UNIT",
  "STDATE",
  "ENDATE",
  "STTIME",
  "ENTIME",
  "CONTROLLER",
])

interface UseTemplateFieldStateParams {
  templateConfig: TemplateConfiguration | null
  extraFieldValues: Record<string, string>
  setExtraFieldValues: Dispatch<SetStateAction<Record<string, string>>>
}

export interface TemplateFieldStateResult {
  generalSections: TemplateSection[]
  workflowSections: TemplateSection[]
  generalFields: TemplateField[]
  additionalGeneralFields: TemplateField[]
  generalFieldMap: Record<string, TemplateField | undefined>
  getStoredTemplateFieldValue: (field: TemplateField) => string | undefined
  getStoredTemplateFieldLabel: (field: TemplateField) => string | undefined
  getTemplateFieldDisplayValue: (field: TemplateField) => string
  setTemplateFieldValue: (
    field: TemplateField,
    value: string | boolean | null | undefined,
  ) => void
  setTemplateFieldLabel: (field: TemplateField, label: string | null | undefined) => void
}

/**
 * Derives template-driven section data (general vs workflow) and exposes utilities
 * for reading/writing dynamic field values stored in `extraFieldValues`.
 */
export const useTemplateFieldState = ({
  templateConfig,
  extraFieldValues,
  setExtraFieldValues,
}: UseTemplateFieldStateParams): TemplateFieldStateResult => {
  const generalSections = useMemo(() => {
    if (!templateConfig?.sections) {
      return [] as TemplateSection[]
    }
    return templateConfig.sections
      .filter((section) => section.isVisible !== false)
      .filter((section) => section.code?.toUpperCase().startsWith("GENERAL"))
      .slice()
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  }, [templateConfig])

  const generalFields = useMemo(() => {
    return generalSections.flatMap((section) =>
      (section.fields ?? [])
        .slice()
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)),
    )
  }, [generalSections])

  const generalFieldMap = useMemo<Record<string, TemplateField | undefined>>(() => {
    const map: Record<string, TemplateField | undefined> = {}
    generalFields.forEach((field) => {
      const upperCode = field.code?.toUpperCase()
      if (!upperCode || map[upperCode]) {
        return
      }
      map[upperCode] = field
    })
    return map
  }, [generalFields])

  const workflowSections = useMemo(() => {
    if (!templateConfig?.sections) {
      return [] as TemplateSection[]
    }
    return templateConfig.sections
      .filter((section) => section.isVisible !== false)
      .filter((section) => !(section.code?.toUpperCase() ?? "").startsWith("GENERAL"))
      .slice()
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  }, [templateConfig])

  const additionalGeneralFields = useMemo(() => {
    return generalFields.filter((field) => {
      const code = field.code?.toUpperCase?.() ?? ""
      if (!code) {
        return true
      }
      return !RENDERED_GENERAL_FIELD_CODES.has(code)
    })
  }, [generalFields])

  const getStoredTemplateFieldValue = useCallback(
    (field: TemplateField): string | undefined => {
      const keys = resolveTemplateFieldKeys(field)
      for (const key of keys) {
        if (key && key in extraFieldValues) {
          return extraFieldValues[key]
        }
      }
      return undefined
    },
    [extraFieldValues],
  )

  const getTemplateFieldDisplayValue = useCallback(
    (field: TemplateField) => getStoredTemplateFieldValue(field) ?? "",
    [getStoredTemplateFieldValue],
  )

  const getStoredTemplateFieldLabel = useCallback(
    (field: TemplateField): string | undefined => {
      const keys = resolveTemplateFieldLabelKeys(field)
      for (const key of keys) {
        if (key && key in extraFieldValues) {
          const value = extraFieldValues[key]
          if (value && value.trim().length > 0) {
            return value
          }
        }
      }
      return undefined
    },
    [extraFieldValues],
  )

  const setTemplateFieldValue = useCallback(
    (field: TemplateField, value: string | boolean | null | undefined) => {
      const keys = resolveTemplateFieldKeys(field)
      if (keys.length === 0) {
        return
      }

      setExtraFieldValues((prev) => {
        let next = prev
        let changed = false

        const ensureWritable = () => {
          if (next === prev) {
            next = { ...prev }
          }
        }

        if (value === null || value === undefined || value === "") {
          keys.forEach((key) => {
            if (key && next[key] !== undefined) {
              ensureWritable()
              delete next[key]
              changed = true
            }
          })
          return changed ? next : prev
        }

        const normalized =
          typeof value === "string"
            ? value
            : typeof value === "boolean"
              ? value
                ? "true"
                : "false"
              : String(value)

        keys.forEach((key, index) => {
          if (!key) {
            return
          }
          if (next[key] !== normalized) {
            ensureWritable()
            next[key] = normalized
            changed = true
          } else if (index === 0 && next !== prev) {
            // already set in a cloned object, no further action
          }
        })

        return changed ? next : prev
      })
    },
    [setExtraFieldValues],
  )

  const setTemplateFieldLabel = useCallback(
    (field: TemplateField, label: string | null | undefined) => {
      const keys = resolveTemplateFieldLabelKeys(field)
      if (keys.length === 0) {
        return
      }

      setExtraFieldValues((prev) => {
        let next = prev
        let changed = false

        const ensureWritable = () => {
          if (next === prev) {
            next = { ...prev }
          }
        }

        const normalized = typeof label === "string" ? label.trim() : ""
        if (!normalized) {
          keys.forEach((key) => {
            if (key && next[key] !== undefined) {
              ensureWritable()
              delete next[key]
              changed = true
            }
          })
          return changed ? next : prev
        }

        keys.forEach((key) => {
          if (!key) {
            return
          }
          if (next[key] !== normalized) {
            ensureWritable()
            next[key] = normalized
            changed = true
          }
        })

        return changed ? next : prev
      })
    },
    [setExtraFieldValues],
  )

  return {
    generalSections,
    workflowSections,
    generalFields,
    additionalGeneralFields,
    generalFieldMap,
    getStoredTemplateFieldValue,
    getStoredTemplateFieldLabel,
    getTemplateFieldDisplayValue,
    setTemplateFieldValue,
    setTemplateFieldLabel,
  }
}
